import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const cardVariants = cva(
  'overflow-hidden bg-white text-gray-900 transition-shadow dark:bg-gray-900 dark:text-gray-100',
  {
    variants: {
      variant: {
        elevated:
          'border border-gray-200 shadow-sm hover:shadow-md dark:border-gray-700',
        outlined: 'border border-gray-200 dark:border-gray-700',
        flat: 'border border-transparent bg-gray-50 dark:bg-gray-800',
      },
      radius: {
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        '2xl': 'rounded-2xl',
      },
      padding: {
        none: 'p-0',
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-6',
      },
      interactive: {
        true: 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'elevated',
      radius: 'lg',
      padding: 'none',
      interactive: false,
    },
  }
)

export interface CardProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, radius, padding, interactive, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        cardVariants({ variant, radius, padding, interactive, className })
      )}
      {...rest}
    />
  )
)
Card.displayName = 'Card'

export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn(
      'border-b border-gray-200 px-4 py-3 dark:border-gray-700',
      className
    )}
    {...rest}
  />
))
CardHeader.displayName = 'CardHeader'

export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...rest }, ref) => (
  <h3
    ref={ref}
    className={cn('text-base font-semibold leading-tight', className)}
    {...rest}
  />
))
CardTitle.displayName = 'CardTitle'

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...rest }, ref) => (
  <p
    ref={ref}
    className={cn('mt-1 text-sm text-gray-500 dark:text-gray-400', className)}
    {...rest}
  />
))
CardDescription.displayName = 'CardDescription'

export const CardBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div ref={ref} className={cn('px-4 py-3', className)} {...rest} />
))
CardBody.displayName = 'CardBody'

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn(
      'border-t border-gray-200 px-4 py-3 dark:border-gray-700',
      className
    )}
    {...rest}
  />
))
CardFooter.displayName = 'CardFooter'

export { cardVariants }
